import { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PencilSimple, Plus, Sparkle, Tag, Trash } from '@phosphor-icons/react';

import { listMyOrganizations } from '@/api/orgs';
import {
  deleteTeamCategory,
  listTeamCategories,
  type TeamCategory,
} from '@/api/teamCategories';
import { Button } from '@/components/ui/button';
import { LoadError } from '@/components/LoadError';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { RowActions, type RowAction } from '@/components/ui/row-actions';
import { useConfirm } from '@/components/ConfirmDialog';
import { askAssistant } from '@/lib/askAssistant';
import { TeamCategorySheet, type CategoryTab } from '@/components/teamCategories/TeamCategorySheet';
import { ClubMembershipFeeCard } from '@/components/teamCategories/ClubMembershipFeeCard';

// Team categories settings page. Per .claude/plans/team-categories-and-
// scheduling-rules.md, Phase 1: org-admin defines a flat list of categories
// (Senior, Youth, …) and assigns each team to at most one. Phase 2 will
// hang scheduling rules off these categories for AI template generation.
//
// Authorization: page itself is open to any org member (so leaders can see
// the bucketing) but mutations (add/edit/delete) only render for owners
// and administrators. The backend enforces the same gate.
export function TeamCategories() {
  const { slug = '' } = useParams();
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  // The edited category + active tab are both URL-driven — ?edit=<id> opens
  // the drawer and ?tab=category|rules selects the tab — so the AI navigate
  // tool can deep-link "open Ungdom's schedule rules" straight to the rules
  // tab. The create-new affordance keeps a separate local flag because
  // there's no entity id for the not-yet-created row.
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);

  const orgsQuery = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });
  const org = orgsQuery.data?.find((o) => o.slug === slug);
  const callerRole = org?.role ?? '';
  const canManage = callerRole === 'owner' || callerRole === 'administrator';

  const categoriesQuery = useQuery({
    queryKey: ['team-categories', slug],
    queryFn: () => listTeamCategories(token, slug),
    enabled: !!token && !!slug,
  });

  const deleteMutation = useMutation({
    mutationFn: ({ c, force }: { c: TeamCategory; force: boolean }) =>
      deleteTeamCategory(token, slug, c.id, { force }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['team-categories', slug] });
      // The cascade path also flips category_id to NULL on every linked team,
      // so the Teams page's cached list needs invalidating regardless.
      void queryClient.invalidateQueries({ queryKey: ['teams', slug] });
    },
  });

  const onDelete = async (c: TeamCategory) => {
    // Branch on team_count: zero teams = plain destructive confirm; non-zero
    // = cascade prompt that tells the user how many teams will become
    // uncategorised before they commit. Server enforces the same gate via
    // ?force=true so a stale snapshot can't sneak through.
    if (c.team_count > 0) {
      const ok = await confirm({
        title: t('settings.teamCategories.confirm.cascadeDeleteTitle'),
        body: t('settings.teamCategories.confirm.cascadeDeleteBody', {
          name: c.name,
          team_count: c.team_count,
        }),
        confirmLabel: t('settings.teamCategories.confirm.cascadeDeleteCta'),
        tone: 'destructive',
      });
      if (!ok) return;
      deleteMutation.mutate({ c, force: true });
      return;
    }
    const ok = await confirm({
      title: t('settings.teamCategories.confirm.deleteTitle'),
      body: t('settings.teamCategories.confirm.deleteBody', { name: c.name }),
      confirmLabel: t('settings.teamCategories.confirm.deleteCta'),
      tone: 'destructive',
    });
    if (!ok) return;
    deleteMutation.mutate({ c, force: false });
  };

  const editTargetId = searchParams.get('edit');
  const editTarget = useMemo(
    () => categoriesQuery.data?.categories.find((c) => c.id === editTargetId),
    [categoriesQuery.data, editTargetId],
  );
  const sheetOpen = createOpen || !!editTarget;
  const tabParam = searchParams.get('tab');
  const activeTab: CategoryTab =
    tabParam === 'rules' || tabParam === 'fees' || tabParam === 'notices'
      ? tabParam
      : 'category';

  const openCreate = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    next.delete('tab');
    setSearchParams(next, { replace: true });
    setCreateOpen(true);
  };
  const openEdit = useCallback(
    (c: TeamCategory, tab: CategoryTab = 'category') => {
      const next = new URLSearchParams(searchParams);
      next.set('edit', c.id);
      if (tab === 'category') next.delete('tab');
      else next.set('tab', tab);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const setTab = useCallback(
    (tab: CategoryTab) => {
      const next = new URLSearchParams(searchParams);
      if (tab === 'category') next.delete('tab');
      else next.set('tab', tab);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  // After create, flip create-mode → edit-mode so the rules tab appears.
  const onCreated = useCallback(
    (saved: TeamCategory) => {
      setCreateOpen(false);
      const next = new URLSearchParams(searchParams);
      next.set('edit', saved.id);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const closeEditOrCreate = useCallback(() => {
    setCreateOpen(false);
    if (searchParams.has('edit') || searchParams.has('tab')) {
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      next.delete('tab');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  // "Suggest categories with AI": dispatch a CustomEvent the AICommandBar
  // (mounted in the dashboard header) listens for. The prompt is fully
  // localised so the model replies in the user's language; the AI then
  // calls list_teams + list_team_categories + list_team_category_templates
  // and proposes one bundled CommandIntent the user approves in-place.
  const onSuggestWithAI = () => askAssistant(t('settings.teamCategories.suggestAIPrompt'));

  const categories = useMemo(
    () => categoriesQuery.data?.categories ?? [],
    [categoriesQuery.data],
  );

  if (!orgsQuery.data && orgsQuery.isPending) {
    return <p className="p-6 text-sm text-muted-foreground">{t('settings.teamCategories.loading')}</p>;
  }
  if (!org) {
    return <p className="p-6 text-sm text-destructive">{t('settings.teamCategories.notFound')}</p>;
  }

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-5 p-4 md:p-8">
      <header className="flex flex-col gap-1">
        <h1 className="font-heading text-2xl font-bold text-foreground">
          {t('settings.teamCategories.title')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('settings.teamCategories.description')}
        </p>
      </header>

      {canManage && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onSuggestWithAI}>
            <Sparkle className="h-4 w-4" aria-hidden weight="fill" />
            {t('settings.teamCategories.suggestAICta')}
          </Button>
          <Button type="button" onClick={openCreate}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('settings.teamCategories.addCta')}
          </Button>
        </div>
      )}

      {categoriesQuery.isPending ? (
        <TableSkeleton columns={4} />
      ) : categoriesQuery.isError ? (
        <LoadError onRetry={() => void categoriesQuery.refetch()} />
      ) : categories.length === 0 ? (
        <>
          <TeamCategoriesEmpty
            canManage={canManage}
            onCreate={openCreate}
            onSuggest={onSuggestWithAI}
          />
          {/* The club-wide membership fee normally lives in a category's
              "Avgifter" tab, next to the fees it sits alongside. With no
              categories there's no drawer to open, so it surfaces here instead
              — otherwise a club that never groups its teams could never set it. */}
          <ClubMembershipFeeCard slug={slug} canManage={canManage} />
        </>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[35%]">{t('settings.teamCategories.column.name')}</TableHead>
                <TableHead>{t('settings.teamCategories.column.description')}</TableHead>
                <TableHead className="w-[10%] text-right">
                  {t('settings.teamCategories.column.teamCount')}
                </TableHead>
                {canManage && <TableHead className="w-[4rem]" aria-hidden />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((c) => {
                // A single "edit" affordance opens the drawer; its tabs cover
                // basics, schedule rules and notices, so the old separate
                // "edit rules" menu item is redundant. Surfaced both as a
                // hover fast-action and in the menu, with delete.
                const editAction: RowAction = {
                  id: 'edit',
                  label: t('settings.teamCategories.actions.edit'),
                  icon: PencilSimple,
                  onSelect: () => openEdit(c),
                };
                const fastActions: RowAction[] = canManage ? [editAction] : [];
                const menuActions: RowAction[] = canManage
                  ? [
                      editAction,
                      {
                        id: 'delete',
                        label: t('settings.teamCategories.actions.delete'),
                        icon: Trash,
                        onSelect: () => {
                          void onDelete(c);
                        },
                        destructive: true,
                      },
                    ]
                  : [];
                return (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium text-foreground">{c.name}</TableCell>
                    <TableCell className="text-muted-foreground">{c.description || '—'}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {c.team_count}
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <RowActions fastActions={fastActions} menuActions={menuActions} />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <TeamCategorySheet
        open={sheetOpen}
        onClose={closeEditOrCreate}
        slug={slug}
        category={editTarget}
        tab={activeTab}
        onTabChange={setTab}
        canManage={canManage}
        onCreated={onCreated}
      />
    </section>
  );
}

function TeamCategoriesEmpty({
  canManage,
  onCreate,
  onSuggest,
}: {
  canManage: boolean;
  onCreate: () => void;
  onSuggest: () => void;
}) {
  const { t } = useTranslation('common');
  return (
    <EmptyState
      icon={<Tag weight="duotone" />}
      title={t('settings.teamCategories.empty.title')}
      body={t('settings.teamCategories.empty.body')}
      actions={
        canManage
          ? [
              {
                label: t('settings.teamCategories.suggestAICta'),
                onClick: onSuggest,
                icon: <Sparkle weight="fill" />,
                variant: 'outline',
              },
              {
                label: t('settings.teamCategories.addCta'),
                onClick: onCreate,
                icon: <Plus weight="bold" />,
              },
            ]
          : undefined
      }
    />
  );
}
